import { useState, useEffect, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  fetchAllInventoryCars,
  fetchVehicleDetailsById,
  type InventoryCarItem,
  type VehicleDetailsItem,
} from "@/data/carsData";
import {
  ChevronLeft,
  Gauge,
  // Calendar,
  Settings2,
  Users,
  Fuel,
  Car,
  Palette,
  Zap,
  Shield,
  ShieldCheck,
  CheckCircle,
  Phone,
  Mail,
  Star,
  Calculator,
  Wrench,
  ChevronDown,
  ChevronUp,
  Share2,
  Link2,
  X,
  // Heart,
  DoorOpen,
  Sun,
  Volume2,
  Monitor,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import "./VehicleDetails.css";

// Scroll reveal hook
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("revealed");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" },
    );

    const observeElements = () => {
      document.querySelectorAll(".scroll-reveal").forEach((el) => {
        if (!el.classList.contains("revealed")) {
          observer.observe(el);
        }
      });
    };

    observeElements();

    const mutationObserver = new MutationObserver(() => {
      observeElements();
    });

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
}

// Accordion component for features
function FeatureAccordion({
  title,
  icon: Icon,
  children,
  isOpen,
  onToggle,
}: {
  title: string;
  icon: React.ElementType;
  children: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <Icon className="w-5 h-5 text-red-600" />
          </div>
          <span className="font-semibold text-gray-900 text-lg">{title}</span>
        </div>
        <div
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${isOpen ? "bg-red-600 text-white" : "bg-gray-100 text-gray-600"}`}
        >
          {isOpen ? (
            <ChevronUp className="w-5 h-5" />
          ) : (
            <ChevronDown className="w-5 h-5" />
          )}
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-[2000px]" : "max-h-0"}`}
      >
        <div className="p-5 border-t border-gray-100">{children}</div>
      </div>
    </div>
  );
}

// Spec card component
function SpecCard({
  icon: Icon,
  label,
  value,
  delay,
}: {
  icon: React.ElementType;
  label: string;
  value: string;
  delay: number;
}) {
  return (
    <div
      className="scroll-reveal bg-white rounded-xl p-5 border border-gray-200 hover:shadow-lg hover:border-red-200 transition-all group"
      style={{ transitionDelay: `${delay}ms` }}
    >
      <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-red-100 transition-colors">
        <Icon className="w-6 h-6 text-gray-600 group-hover:text-red-600 transition-colors" />
      </div>
      <p className="text-sm text-gray-500 mb-1">{label}</p>
      <p className="font-semibold text-gray-900">{value}</p>
    </div>
  );
}

const getFeatureSectionKey = (title: string) =>
  title
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

function getFeatureAccordionIcon(title: string): React.ElementType {
  switch (title.trim().toLowerCase()) {
    case "mechanical":
      return Wrench;
    case "exterior":
      return Sun;
    case "entertainment":
      return Volume2;
    case "interior":
      return Monitor;
    case "safety":
      return Shield;
    case "comfort":
      return Star;
    default:
      return Star;
  }
}

const getInstallmentHeading = (type?: string) => {
  const cleaned = type?.trim() ?? "";
  if (!cleaned) return "";
  return `Est. ${cleaned.replace(/^by\s+/i, "")}`;
};

const getInstallmentMeta = (percentage?: string, duration?: number | null) => {
  const pct = percentage?.trim() ?? "";
  const dur = duration ?? null;

  if (pct && dur !== null) return `${pct}% for ${dur} months`;
  if (pct) return `${pct}%`;
  if (dur !== null) return `${dur}`;
  return "";
};

export default function VehicleDetails() {
  useScrollReveal();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  // const [isLiked, setIsLiked] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showDesktopNameRibbon, setShowDesktopNameRibbon] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  const heroSectionRef = useRef<HTMLElement | null>(null);
  const ribbonEndRef = useRef<HTMLDivElement | null>(null);
  const shareMenuRef = useRef<HTMLDivElement | null>(null);

  const [vehicle, setVehicle] = useState<VehicleDetailsItem | null>(null);
  const [similarVehicles, setSimilarVehicles] = useState<InventoryCarItem[]>(
    [],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const galleryImages = useMemo(() => {
    if (!vehicle) return [];
    if (vehicle.images?.length) return vehicle.images;
    return vehicle.image ? [vehicle.image] : [];
  }, [vehicle]);

  const featureSections = useMemo(() => {
    const sectionMap = new Map<
      string,
      { key: string; title: string; items: string[] }
    >();

    (vehicle?.features ?? []).forEach((feature) => {
      const title = feature.type.trim() || "Other Features";
      const key = getFeatureSectionKey(title);
      const name = feature.name.trim();

      if (!name) return;

      if (!sectionMap.has(key)) {
        sectionMap.set(key, {
          key,
          title,
          items: [],
        });
      }

      const section = sectionMap.get(key)!;

      if (!section.items.includes(name)) {
        section.items.push(name);
      }
    });

    return Array.from(sectionMap.values());
  }, [vehicle]);

  useEffect(() => {
    if (featureSections.length === 0) {
      setOpenAccordion(null);
      return;
    }

    setOpenAccordion((prev) =>
      prev && featureSections.some((section) => section.key === prev)
        ? prev
        : featureSections[0].key,
    );
  }, [featureSections]);

  useEffect(() => {
    let isMounted = true;

    const loadVehicle = async () => {
      const vehicleId = (id ?? "").trim();

      if (!vehicleId) {
        if (isMounted) {
          setVehicle(null);
          setSimilarVehicles([]);
          setLoading(false);
          setError("");
        }
        return;
      }

      try {
        setLoading(true);
        setError("");
        setSelectedImageIndex(0);

        const [vehicleData, allCars] = await Promise.all([
          fetchVehicleDetailsById(vehicleId),
          fetchAllInventoryCars({ ordBy: 2 }),
        ]);

        if (!isMounted) return;

        setVehicle(vehicleData);

        if (vehicleData) {
          setSimilarVehicles(
            allCars
              .filter(
                (car) =>
                  car.id !== vehicleData.id &&
                  (car.make === vehicleData.make ||
                    car.bodyStyle === vehicleData.bodyStyle),
              )
              .slice(0, 3),
          );
        } else {
          setSimilarVehicles([]);
          setError("Vehicle not found.");
        }
      } catch (err) {
        if (!isMounted) return;

        setVehicle(null);
        setSimilarVehicles([]);
        setError(
          err instanceof Error ? err.message : "Unable to load vehicle.",
        );
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadVehicle();

    return () => {
      isMounted = false;
    };
  }, [id]);

  useEffect(() => {
    const desktopMq = window.matchMedia("(min-width: 1280px)");

    const updateFloatingRibbon = () => {
      const heroEl = heroSectionRef.current;
      const endEl = ribbonEndRef.current;

      if (!heroEl || !endEl || !desktopMq.matches) {
        setShowDesktopNameRibbon(false);
        return;
      }

      const heroRect = heroEl.getBoundingClientRect();
      const endRect = endEl.getBoundingClientRect();

      const startOffset = 150;
      const endOffset = 180;

      const hasPassedHeroSection = heroRect.bottom <= startOffset;
      const isBeforeRibbonEnd = endRect.top > endOffset;

      setShowDesktopNameRibbon(hasPassedHeroSection && isBeforeRibbonEnd);
    };

    updateFloatingRibbon();

    window.addEventListener("scroll", updateFloatingRibbon, { passive: true });
    window.addEventListener("resize", updateFloatingRibbon);

    if (typeof desktopMq.addEventListener === "function") {
      desktopMq.addEventListener("change", updateFloatingRibbon);
    } else {
      desktopMq.addListener(updateFloatingRibbon);
    }

    return () => {
      window.removeEventListener("scroll", updateFloatingRibbon);
      window.removeEventListener("resize", updateFloatingRibbon);

      if (typeof desktopMq.removeEventListener === "function") {
        desktopMq.removeEventListener("change", updateFloatingRibbon);
      } else {
        desktopMq.removeListener(updateFloatingRibbon);
      }
    };
  }, [vehicle?.id, similarVehicles.length]);

  useEffect(() => {
    if (!shareMenuOpen) return;

    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target;
      if (
        shareMenuRef.current &&
        target instanceof Node &&
        !shareMenuRef.current.contains(target)
      ) {
        setShareMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShareMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [shareMenuOpen]);

  const shareTitle =
    `${vehicle?.year ?? ""} ${vehicle?.make ?? ""} ${vehicle?.model ?? ""}`.trim();

  const getShareUrl = () =>
    typeof window !== "undefined" ? window.location.href : "";

  const copyPageLink = async () => {
    try {
      const url = getShareUrl();
      if (!url) return;

      await navigator.clipboard.writeText(url);
      setCopiedLink(true);

      window.setTimeout(() => {
        setCopiedLink(false);
      }, 1800);
    } catch {
      setCopiedLink(false);
    }
  };

  const buildShareLink = (
    type: "whatsapp" | "email" | "pinterest" | "facebook" | "x",
  ) => {
    const url = getShareUrl();
    const encodedUrl = encodeURIComponent(url);
    const encodedTitle = encodeURIComponent(shareTitle);

    switch (type) {
      case "whatsapp":
        return `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`;
      case "email":
        return `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A${encodedUrl}`;
      case "pinterest":
        return `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`;
      case "facebook":
        return `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
      case "x":
        return `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`;
      default:
        return url;
    }
  };

  const ShareIconWrap = ({
    children,
    className,
  }: {
    children: React.ReactNode;
    className: string;
  }) => (
    <span
      className={`flex h-10 w-10 items-center justify-center rounded-full text-white shadow-sm ${className}`}
    >
      {children}
    </span>
  );

  const WhatsAppIcon = () => (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 fill-current"
      aria-hidden="true"
    >
      <path d="M20.52 3.48A11.8 11.8 0 0 0 12.07 0C5.51 0 .16 5.34.16 11.91c0 2.1.55 4.15 1.6 5.96L0 24l6.31-1.65a11.9 11.9 0 0 0 5.76 1.47h.01c6.56 0 11.91-5.34 11.91-11.91 0-3.18-1.24-6.17-3.47-8.43ZM12.08 21.8h-.01a9.9 9.9 0 0 1-5.05-1.39l-.36-.21-3.74.98 1-3.65-.23-.38a9.9 9.9 0 0 1-1.52-5.24c0-5.46 4.44-9.9 9.9-9.9 2.64 0 5.12 1.03 6.99 2.9a9.83 9.83 0 0 1 2.9 7c0 5.46-4.44 9.9-9.9 9.9Zm5.43-7.4c-.3-.15-1.77-.87-2.05-.96-.27-.1-.47-.15-.67.15-.2.3-.76.96-.94 1.16-.17.2-.35.22-.64.08-.3-.15-1.24-.46-2.36-1.46-.87-.78-1.46-1.74-1.63-2.03-.17-.3-.02-.45.13-.6.13-.13.3-.35.44-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.07-.15-.67-1.62-.92-2.21-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.08-.8.37-.27.3-1.04 1.01-1.04 2.46s1.07 2.86 1.22 3.06c.15.2 2.08 3.18 5.03 4.46.7.3 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.77-.72 2.02-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
    </svg>
  );

  const PinterestIcon = () => (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 fill-current"
      aria-hidden="true"
    >
      <path d="M12.04 0C5.4 0 0 5.4 0 12.04c0 4.89 2.93 9.1 7.13 10.96-.1-.93-.18-2.36.04-3.38.2-.88 1.28-5.6 1.28-5.6s-.33-.66-.33-1.63c0-1.53.88-2.67 1.99-2.67.94 0 1.39.7 1.39 1.55 0 .94-.6 2.35-.91 3.66-.26 1.1.55 2 1.63 2 1.95 0 3.45-2.06 3.45-5.03 0-2.63-1.89-4.47-4.6-4.47-3.13 0-4.97 2.35-4.97 4.78 0 .94.36 1.95.82 2.5.09.11.1.21.07.33-.08.37-.26 1.18-.3 1.34-.05.22-.16.27-.38.16-1.42-.66-2.3-2.74-2.3-4.41 0-3.59 2.61-6.88 7.52-6.88 3.95 0 7.02 2.81 7.02 6.58 0 3.92-2.47 7.08-5.9 7.08-1.15 0-2.23-.6-2.6-1.31l-.7 2.67c-.25.98-.94 2.2-1.4 2.95 1.05.33 2.16.5 3.32.5 6.64 0 12.04-5.4 12.04-12.04S18.68 0 12.04 0Z" />
    </svg>
  );

  const FacebookIcon = () => (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 fill-current"
      aria-hidden="true"
    >
      <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07c0 6.03 4.39 11.03 10.12 11.93v-8.43H7.08v-3.5h3.04V9.41c0-3.01 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.95h-1.51c-1.49 0-1.95.93-1.95 1.88v2.26h3.32l-.53 3.5h-2.79V24C19.61 23.1 24 18.1 24 12.07Z" />
    </svg>
  );

  const XIcon = () => (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5 fill-current"
      aria-hidden="true"
    >
      <path d="M18.9 2H22l-6.77 7.74L23.2 22h-6.26l-4.9-6.43L6.4 22H3.28l7.24-8.28L.8 2h6.42l4.43 5.84L18.9 2Zm-1.1 18h1.72L6.26 3.9H4.4L17.8 20Z" />
    </svg>
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-32 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Car className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Loading Vehicle...
          </h1>
          <p className="text-gray-600">
            Please wait while we fetch the details.
          </p>
        </div>
      </div>
    );
  }
  if (!vehicle) {
    return (
      <div className="min-h-screen pt-32 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
            <Car className="w-12 h-12 text-gray-400" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Vehicle Not Found
          </h1>
          <p className="text-gray-600 mb-8">
            {error || `Sorry, we couldn't find a vehicle with ID: ${id}`}
          </p>
          <button
            onClick={() => navigate("/car-inventory")}
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-red-700 transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
            Back to Inventory
          </button>
        </div>
      </div>
    );
  }

  const toggleAccordion = (key: string) => {
    setOpenAccordion(openAccordion === key ? null : key);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb & Actions Bar */}
      <div className="sticky top-20 z-40 bg-gray-50 pt-4 sm:pt-5">
        <div className="bg-white border-b border-gray-200 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate("/car-inventory")}
                className="flex items-center gap-2 text-gray-600 hover:text-red-600 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
                <span className="font-medium">Back to Inventory</span>
              </button>

              <div className="flex items-center gap-3">
                <div ref={shareMenuRef} className="relative">
                  <button
                    type="button"
                    onClick={() => setShareMenuOpen((prev) => !prev)}
                    aria-haspopup="dialog"
                    aria-expanded={shareMenuOpen}
                    aria-label="Share this vehicle"
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:bg-gray-200 transition-colors"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>

                  {shareMenuOpen && (
                    <div className="absolute right-0 top-[calc(100%+12px)] z-[70] w-[min(320px,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            Share Vehicle
                          </p>
                          <p className="text-xs text-gray-500">
                            Send this vehicle page instantly
                          </p>
                        </div>

                        <button
                          type="button"
                          onClick={() => setShareMenuOpen(false)}
                          className="rounded-full p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
                          aria-label="Close share menu"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>

                      <div className="p-3">
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                          <a
                            href={buildShareLink("whatsapp")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-green-300 hover:bg-green-50"
                          >
                            <ShareIconWrap className="bg-green-500 group-hover:bg-green-600">
                              <WhatsAppIcon />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              WhatsApp
                            </span>
                          </a>

                          <a
                            href={buildShareLink("email")}
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50"
                          >
                            <ShareIconWrap className="bg-gray-900 group-hover:bg-black">
                              <Mail className="h-5 w-5" />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              Email
                            </span>
                          </a>

                          <a
                            href={buildShareLink("pinterest")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50"
                          >
                            <ShareIconWrap className="bg-[#E60023] group-hover:bg-[#cc001f]">
                              <PinterestIcon />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              Pinterest
                            </span>
                          </a>

                          <a
                            href={buildShareLink("facebook")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-blue-300 hover:bg-blue-50"
                          >
                            <ShareIconWrap className="bg-[#1877F2] group-hover:bg-[#1668d8]">
                              <FacebookIcon />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              Facebook
                            </span>
                          </a>

                          <a
                            href={buildShareLink("x")}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50"
                          >
                            <ShareIconWrap className="bg-black group-hover:bg-gray-800">
                              <XIcon />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              X
                            </span>
                          </a>

                          <button
                            type="button"
                            onClick={copyPageLink}
                            className="group flex flex-col items-center justify-center rounded-2xl border border-gray-200 bg-white px-3 py-3 text-center transition-all hover:-translate-y-0.5 hover:border-red-300 hover:bg-red-50"
                          >
                            <ShareIconWrap className="bg-brand-red group-hover:bg-red-700">
                              <Link2 className="h-5 w-5" />
                            </ShareIconWrap>
                            <span className="mt-2 text-xs font-semibold text-gray-800">
                              {copiedLink ? "Copied" : "Copy Link"}
                            </span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className={`vehicle-name-ribbon-shell ${
          showDesktopNameRibbon ? "is-visible" : ""
        }`}
        aria-hidden={!showDesktopNameRibbon}
      >
        <div className="vehicle-name-ribbon">
          <span className="vehicle-name-ribbon__text">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </span>
        </div>
      </div>

      {/* Hero Section */}
      <section
        ref={heroSectionRef}
        className="vehicle-hero-section pt-5 sm:pt-7 lg:pt-8 pb-10 sm:pb-12"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="vehicle-hero-grid grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-start">
            {/* Left - Image Gallery */}
            <div className="scroll-reveal min-w-0">
              <div className="vehicle-gallery-main relative rounded-[24px] sm:rounded-3xl overflow-hidden shadow-2xl bg-gray-900">
                <img
                  src={galleryImages[selectedImageIndex] || galleryImages[0]}
                  alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} - image ${selectedImageIndex + 1}`}
                  className="vehicle-gallery-image w-full h-[250px] sm:h-[340px] md:h-[420px] lg:h-[500px] object-cover"
                />

                {/* Overlays */}
                <div className="absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent" />

                {/* Badges */}
                <div className="vehicle-hero-badges absolute top-3 left-3 sm:top-4 sm:left-4 flex flex-wrap gap-2 max-w-[calc(100%-24px)] sm:max-w-none">
                  {vehicle.isGoodPrice && (
                    <Badge className="bg-green-500 text-white px-3 py-1 text-[11px] sm:text-xs">
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      {vehicle.tagLabel || "GOOD PRICE"}
                    </Badge>
                  )}
                  <Badge className="bg-red-600 text-white px-3 py-1 text-[11px] sm:text-xs">
                    {vehicle.condition}
                  </Badge>
                </div>

                {/* Watch Video Button */}
                {/* <button className="vehicle-watch-video absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-white/90 backdrop-blur-sm text-gray-900 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full font-medium text-sm sm:text-base hover:bg-white transition-colors whitespace-nowrap">
                  <Play className="w-4 h-4 fill-current" />
                  Watch Video
                </button> */}
              </div>

              {/* Thumbnail Strip */}
              {galleryImages.length > 1 && (
                <div className="thumbnail-scroll flex gap-2.5 sm:gap-3 mt-3 sm:mt-4 overflow-x-auto pb-2">
                  {galleryImages.map((img, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setSelectedImageIndex(index)}
                      className={`vehicle-thumb flex-shrink-0 w-20 h-14 sm:w-24 sm:h-16 rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                        selectedImageIndex === index
                          ? "border-red-600 ring-2 ring-red-200"
                          : "border-transparent hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${vehicle.year} ${vehicle.make} ${vehicle.model} thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right - Vehicle Info */}
            <div
              className="vehicle-info-panel scroll-reveal min-w-0 pt-4 sm:pt-5 lg:pt-6"
              style={{ transitionDelay: "100ms" }}
            >
              {/* Title Section */}
              <div className="mb-5 sm:mb-6">
                <div className="vehicle-stock-vin flex flex-wrap items-center gap-2 sm:gap-3 mb-3">
                  {/* Mobile: show only Reg No */}
                  <Badge
                    variant="outline"
                    className="inline-flex sm:hidden text-gray-600 text-[11px] max-w-full"
                  >
                    <Car className="w-3 h-3 mr-1 shrink-0" />
                    Reg No: {vehicle.stockNumber}
                  </Badge>
                </div>

                <h1 className="vehicle-hero-title text-[1.95rem] sm:text-4xl lg:text-5xl font-bold leading-[1.12] sm:leading-[1.18] text-gray-900 mb-2 pt-4">
                  {vehicle.year} {vehicle.make} {vehicle.model}
                </h1>
                <p className="vehicle-hero-trim text-lg sm:text-xl text-gray-600">
                  {vehicle.trim}
                </p>
              </div>

              {/* Pricing Section */}
              <div className="vehicle-pricing-card bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-4 sm:p-6 mb-5 sm:mb-6">
                <div className="vehicle-pricing-top flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-4">
                  <div>
                    <p className="text-gray-400 text-xs sm:text-sm mb-1">
                      Dealer Price
                    </p>
                    <p className="text-3xl sm:text-4xl font-bold text-white leading-none">
                      {vehicle.price !== null
                        ? `$${vehicle.price.toLocaleString()}`
                        : ""}
                    </p>
                    <p className="text-gray-400 text-xs sm:text-sm mt-1">
                      + taxes and fees
                    </p>
                  </div>

                  <div className="vehicle-pricing-side text-left sm:text-right">
                    <p className="text-gray-400 text-xs sm:text-sm mb-1 min-h-[20px]">
                      {getInstallmentHeading(vehicle.installmentType)}
                    </p>

                    <p className="text-2xl sm:text-3xl font-bold text-green-400 leading-none min-h-[36px]">
                      {vehicle.installmentAmount !== null
                        ? `$${vehicle.installmentAmount.toLocaleString()}`
                        : ""}
                    </p>

                    <p className="text-gray-400 text-xs sm:text-sm mt-1 min-h-[20px]">
                      {getInstallmentMeta(
                        vehicle.installmentPercentage,
                        vehicle.installmentDuration,
                      )}
                    </p>
                  </div>
                </div>

                {/* <button className="w-full sm:w-auto text-left text-gray-400 text-sm hover:text-white transition-colors inline-flex items-center gap-1">
                  Estimate Your Payments
                  <ArrowRight className="w-4 h-4" />
                </button> */}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                <button
                  onClick={() => navigate("/finance#auto-loan-calculator")}
                  className="vehicle-primary-action w-full flex items-center justify-center gap-2 bg-red-600 text-white px-5 sm:px-6 py-3.5 sm:py-4 rounded-xl font-bold text-base sm:text-lg hover:bg-red-700 transition-all hover:shadow-lg hover:shadow-red-500/30"
                >
                  <Calculator className="w-5 h-5 shrink-0" />
                  Auto Loan Calculator
                </button>

                {vehicle.carfxUrl && (
                  <a
                    href={vehicle.carfxUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vehicle-history-badge-wrap vehicle-history-badge-wrap--link"
                    aria-label="Open vehicle history report"
                  >
                    <img
                      src="/images/car-fax-logo.jpeg"
                      alt="Vehicle History"
                      className="vehicle-history-badge-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </a>
                )}
              </div>

              {/* Quick Contact */}
              <div className="vehicle-contact-row mt-5 sm:mt-6 flex flex-wrap items-center justify-start sm:justify-center gap-4 sm:gap-6 text-sm text-gray-600">
                <a
                  href="tel:6043588081"
                  className="flex items-center gap-2 hover:text-red-600 transition-colors"
                >
                  <Phone className="w-4 h-4 shrink-0" />
                  (604) 358-8081
                </a>
                <a
                  href="mailto:info@bigboysmotorclub.com"
                  className="flex items-center gap-2 hover:text-red-600 transition-colors"
                >
                  <Mail className="w-4 h-4 shrink-0" />
                  Email Us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vehicle Specs Grid */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 scroll-reveal">
            <Badge className="mb-4 bg-red-100 text-red-700 border-0">
              <Gauge className="w-4 h-4 mr-2" />
              Specifications
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Everything You Need to <span className="text-red-600">Know</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <SpecCard
              icon={Gauge}
              label="Kilometres"
              value={vehicle.km !== null ? vehicle.km.toLocaleString() : ""}
              delay={0}
            />
            <SpecCard
              icon={CheckCircle}
              label="Condition"
              value={vehicle.condition}
              delay={50}
            />
            <SpecCard
              icon={Car}
              label="Body Style"
              value={vehicle.bodyStyle}
              delay={100}
            />
            <SpecCard
              icon={Zap}
              label="Engine"
              value={vehicle.engine}
              delay={150}
            />
            <SpecCard
              icon={Palette}
              label="Exterior Colour"
              value={vehicle.exteriorColor}
              delay={200}
            />
            <SpecCard
              icon={Settings2}
              label="Transmission"
              value={vehicle.transmission}
              delay={250}
            />
            <SpecCard
              icon={Shield}
              label="Drive Train"
              value={vehicle.drivetrain}
              delay={300}
            />
            <SpecCard
              icon={Users}
              label="Passengers"
              value={vehicle.passengers || ""}
              delay={350}
            />
            <SpecCard
              icon={DoorOpen}
              label="Doors"
              value={vehicle.doors || ""}
              delay={400}
            />
            <SpecCard
              icon={Fuel}
              label="Fuel Type"
              value={vehicle.fuelType}
              delay={450}
            />
            <SpecCard
              icon={Gauge}
              label="City Economy"
              value={vehicle.mileage || ""}
              delay={500}
            />
            <SpecCard
              icon={ShieldCheck}
              label="Insurance"
              value={vehicle.insurance || ""}
              delay={550}
            />
          </div>

          {/* Description */}
          <div className="mt-8 scroll-reveal">
            <div className="bg-gray-50 rounded-xl p-6 border-l-4 border-red-600">
              <p className="text-gray-700 leading-relaxed">
                {vehicle.description || ""}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features & Equipment Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 scroll-reveal">
            <Badge className="mb-4 bg-blue-100 text-blue-700 border-0">
              <Wrench className="w-4 h-4 mr-2" />
              Standard Equipment
            </Badge>
            <h2 className="text-3xl font-bold text-gray-900">
              Features & <span className="text-red-600">Equipment</span>
            </h2>
          </div>

          <div className="space-y-4 scroll-reveal">
            {featureSections.length > 0 ? (
              featureSections.map((section) => {
                const Icon = getFeatureAccordionIcon(section.title);

                return (
                  <FeatureAccordion
                    key={section.key}
                    title={section.title}
                    icon={Icon}
                    isOpen={openAccordion === section.key}
                    onToggle={() => toggleAccordion(section.key)}
                  >
                    <div className="grid md:grid-cols-2 gap-3">
                      {section.items.map((feature, index) => (
                        <div
                          key={`${section.key}-${index}`}
                          className="flex items-center gap-3 text-gray-700"
                        >
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </FeatureAccordion>
                );
              })
            ) : (
              <div className="bg-white border border-gray-200 rounded-xl p-6 text-center text-gray-500">
                No features available for this vehicle.
              </div>
            )}
          </div>

          {/* Disclaimer */}
          <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200 scroll-reveal">
            <p className="text-sm text-yellow-800">
              <strong>Disclaimer:</strong> Standard Equipment is the default
              equipment supplied for the Make and Model of this vehicle, but may
              not represent the final vehicle with additional / altered
              equipment options. Please contact the dealership for verification
              of any information listed herein.
            </p>
          </div>
        </div>
      </section>

      {/* Similar Vehicles Section */}
      {similarVehicles.length > 0 && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-10 scroll-reveal">
              <Badge className="mb-4 bg-green-100 text-green-700 border-0">
                <Car className="w-4 h-4 mr-2" />
                You May Also Like
              </Badge>
              <h2 className="text-3xl font-bold text-gray-900">
                Similar <span className="text-red-600">Vehicles</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {similarVehicles.map((car, index) => (
                <div
                  key={car.id}
                  className="scroll-reveal bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                  style={{ transitionDelay: `${index * 100}ms` }}
                  onClick={() => navigate(`/vehicle-details/${car.id}`)}
                >
                  <div className="relative h-48 overflow-hidden">
                    {car.image ? (
                      <img
                        src={car.image}
                        alt={`${car.year} ${car.make} ${car.model}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100" />
                    )}
                    {car.isGoodPrice && (
                      <Badge className="absolute top-3 left-3 bg-green-500 text-white">
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        GOOD PRICE
                      </Badge>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-bold text-gray-900 mb-1">
                      {car.year} {car.make} {car.model}
                    </h3>
                    <p className="text-gray-500 text-sm mb-3">{car.trim}</p>
                    <div className="flex items-center justify-between">
                      <p className="text-xl font-bold text-red-600">
                        {car.price !== null
                          ? `$${car.price.toLocaleString()}`
                          : ""}
                      </p>
                      <p className="text-sm text-gray-500">
                        {car.km !== null ? `${car.km.toLocaleString()} km` : ""}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div
        ref={ribbonEndRef}
        className="vehicle-name-ribbon-end"
        aria-hidden="true"
      />

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 rounded-3xl overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl" />
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-yellow-400 rounded-full filter blur-3xl" />
            </div>

            <div className="relative z-10 px-8 py-12 md:px-16 md:py-16 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Ready to Take This {vehicle.make} for a Spin?
              </h2>
              <p className="text-xl text-white/90 max-w-2xl mx-auto mb-8">
                Schedule a test drive today and experience this {vehicle.year}{" "}
                {vehicle.make} {vehicle.model} firsthand. Our team is ready to
                assist you!
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:6043588081"
                  className="inline-flex items-center justify-center gap-2 bg-white text-red-600 px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl transition-all"
                >
                  <Phone className="w-5 h-5" />
                  Call (604) 358-8081
                </a>
                {/* <button className="inline-flex items-center justify-center gap-2 bg-white/20 backdrop-blur-sm text-white border-2 border-white/50 px-8 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all">
                  <Calendar className="w-5 h-5" />
                  Schedule Test Drive
                </button> */}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Bottom Disclaimer */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="scroll-reveal p-6 bg-gray-100 rounded-xl">
            <p className="text-sm text-gray-600 leading-relaxed">
              Please contact the dealership for verification of any information
              listed herein. Due to human error and other possible
              complications, we cannot guarantee the accuracy of the information
              included herein, despite all reasonable attempts being made to
              ensure its accuracy. The information may differ in specification,
              price, or any other listed characteristic herein.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
